import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import PrivateRoute from 'components/Navigation/ProtectedRoute';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';
import Table from 'components/Table';

import { formatDate } from 'utils/functions';
import { ISerie, IVoucher } from 'utils/interfaces';
import { DOC_TYPES, REQUIRED_RULE, VOUCHER_STATUS_TEXT } from 'utils/constants';
import { BUTTON_COLORS, VOUCHER_STATUS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import { useForm } from 'react-hook-form';
import BaseInput from 'components/Inputs/BaseInput';
import Modal from 'components/Modal';

export default function VouchersPage() {
  const {
    register,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      doc_type: 'C',
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [operations, setOperations] = useState<Array<IVoucher>>([]);
  const [series, setSeries] = useState<Array<ISerie>>([]);

  const [email, setEmail] = useState('');
  const [loadingId, setLoadingId] = useState('');
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [action, setAction] = useState('');
  const [actionData, setActionData] = useState<IVoucher | undefined>(undefined);
  const [actionLoading, setActionLoading] = useState(false);

  const docType = watch('doc_type');

  const finalSeries = useMemo(
    () =>
      series.filter(
        (serie) => serie.doc_type === docType && serie.serie[0] === actionData?.doc_type
      ),
    [series, docType, actionData]
  );

  const submitVoucher = async (voucherId: string, load: boolean) => {
    if (load) setLoadingId(voucherId);
    let result: any = await generalApi.post('/submit_voucher', {
      voucherId,
    });
    setLoadingId('');
    if (!result.success) return showErrorToast(result.data.err);
    if (load) {
      showSuccessToast('Comprobante emitido');
    }
    setRefresh(true);
  };

  const submitNote = async (data: any) => {
    setActionLoading(true);
    let result: any = await generalApi.post('/emit_note', {
      voucherId: actionData?._id,
      ...data,
    });
    setActionLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    submitVoucher(result.data.voucherId, false);
    showSuccessToast('Nota creada');
    setIsActionOpen(false);
    setActionData(undefined);
    setRefresh(true);
  };

  const submitVoid = async () => {
    setActionLoading(true);
    let result: any = await generalApi.post('/cancel_voucher', {
      external_id: actionData?.external_id,
      comment: '-',
    });
    setActionLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('Comprobante anulado');
    setIsActionOpen(false);
    setActionData(undefined);
    setRefresh(true);
  };

  const submitMail = async () => {
    if (!email) return showErrorToast('Ingrese un correo electrónico');
    setActionLoading(true);
    let result: any = await generalApi.post('/email_voucher', {
      voucherId: actionData?._id,
      email,
    });
    setActionLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('Comprobante enviado al correo escogido');
    setIsActionOpen(false);
    setActionData(undefined);
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        control={control}
        watch={watch}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  useEffect(() => {
    const getOperations = async () => {
      setLoading(true);
      const result = await generalApi.get(`/vouchers?page=${currentPage}&limit=10`);
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setOperations(result.data.docs);
      setTotalDocs(result.data.totalDocs);
      setLoading(false);
    };
    const getSeries = async () => {
      const result = await generalApi.get('/series');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      setSeries(result.data);
    };
    if (refresh) {
      getSeries();
      getOperations();
      setRefresh(false);
    }
  }, [generalApi, refresh, currentPage]);

  useEffect(() => {
    setValue('serieId', finalSeries[0]?._id);
  }, [finalSeries, setValue]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal
          isOpen={isActionOpen && action === 'note'}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <form onSubmit={handleSubmit(submitNote)}>
            <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
              <div className="mt-3 text-center sm:mt-0 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                  Emitir comprobante
                </h3>
              </div>
            </div>
            <div onSubmit={handleSubmit(submitNote)} className="space-y-4 px-4 py-5 sm:p-6">
              {buildBaseInput('Tipo de comprobante', 'select', 'doc_type', REQUIRED_RULE, {
                options: [
                  { value: 'C', name: 'Nota de Crédito' },
                  { value: 'D', name: 'Nota de Débito' },
                ],
              })}
              {buildBaseInput('Serie', 'select', 'serieId', REQUIRED_RULE, {
                options: finalSeries.map((obj) => ({ value: obj._id, name: obj.serie })),
              })}
              {buildBaseInput('Motivo', 'select', 'note_type', REQUIRED_RULE, {
                options:
                  actionData?.doc_type === 'B'
                    ? [
                        { value: '01', name: 'Anulación de la operación' },
                        { value: '02', name: 'Anulación por error en el RUC' },
                        { value: '03', name: 'Corrección por error en la descripción' },
                        { value: '06', name: 'Devolución total' },
                        { value: '07', name: 'Devolución parcial' },
                        { value: '09', name: 'Disminución en el valor' },
                      ]
                    : [
                        { value: '01', name: 'Anulación de la operación' },
                        { value: '02', name: 'Anulación por error en el RUC' },
                        { value: '03', name: 'Corrección por error en la descripción' },
                        { value: '04', name: 'Descuento global' },
                        { value: '05', name: 'Descuento por ítem' },
                        { value: '06', name: 'Devolución total' },
                        { value: '07', name: 'Devolución parcial' },
                        { value: '08', name: 'Bonificación' },
                        { value: '09', name: 'Disminución en el valor' },
                      ],
              })}
              {buildBaseInput(
                'Descripción del Motivo',
                'text',
                'note_description',
                REQUIRED_RULE,
                {}
              )}
              {buildBaseInput(
                'Descripcion de la Transaccion',
                'text',
                'description',
                REQUIRED_RULE,
                {}
              )}
              {buildBaseInput('Tipo de cambio', 'number', 'change', REQUIRED_RULE, {})}
              {buildBaseInput('Monto Final', 'number', 'amount', REQUIRED_RULE, {})}
            </div>
            <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <SolidButton
                className="w-auto sm:ml-3"
                isLoading={actionLoading}
                onClick={() => handleSubmit(submitNote)}
              >
                Emitir
              </SolidButton>
              <button
                type="button"
                onClick={() => {
                  setIsActionOpen(false);
                  setActionData(undefined);
                }}
                className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </form>
        </Modal>
        <Modal
          isOpen={isActionOpen && action === 'void'}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <div className="rounded-t-lg bg-red-500 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                Confirmar anulación
              </h3>
            </div>
          </div>
          <div>
            <div className="px-4 py-5 sm:p-6">
              <p>{`¿Está seguro que desea anular el comprobante ${actionData?.serie}?`}</p>
            </div>
            <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <SolidButton
                className="w-auto sm:ml-3"
                color={BUTTON_COLORS.RED}
                isLoading={actionLoading}
                onClick={() => submitVoid()}
              >
                Anular
              </SolidButton>
              <button
                type="button"
                onClick={() => {
                  setIsActionOpen(false);
                  setActionData(undefined);
                }}
                className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
        <Modal
          isOpen={isActionOpen && action === 'mail'}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                Enviar comprobante por correo
              </h3>
            </div>
          </div>
          <div>
            <div className="px-4 py-5 sm:p-6">
              <div className="relative h-[fit-content] rounded-lg border border-gray-400 bg-white px-3 py-2 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
                <div className="peer">
                  <div className="relative mt-1">
                    <input
                      id="email_input"
                      type="email"
                      name="email"
                      className="peer block w-full border-0 bg-white p-0 text-gray-900 placeholder-gray-500 focus:ring-0"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
                <label
                  htmlFor="inputId"
                  className={`${
                    email !== undefined && (typeof email !== 'string' || email?.length > 0)
                      ? '-top-3 left-3 py-1 text-sm text-gray-900'
                      : 'top-3 left-2 text-gray-400 peer-focus-within:-top-3 peer-focus-within:left-3 peer-focus-within:py-1 peer-focus-within:text-sm peer-focus-within:text-gray-900'
                  } pointer-events-none absolute -mt-px inline-block rounded-md bg-white px-2 font-medium transition-all duration-150`}
                >
                  Correo
                </label>
              </div>
            </div>
            <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <SolidButton
                className="w-auto sm:ml-3"
                isLoading={actionLoading}
                onClick={() => submitMail()}
              >
                Enviar
              </SolidButton>
              <button
                type="button"
                onClick={() => {
                  setIsActionOpen(false);
                  setActionData(undefined);
                }}
                className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Lista de Comprobantes</p>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando comprobantes"
            initialSort={{
              key: 'created_at',
              order: 'desc',
            }}
            currentPage={currentPage}
            totalDocs={totalDocs}
            onPageChange={(page) => {
              setCurrentPage(page);
              setRefresh(true);
            }}
            columns={[
              {
                title: 'Estado',
                dataKey: 'status',
                render: (item: IVoucher) => VOUCHER_STATUS_TEXT[item.status as VOUCHER_STATUS],
              },
              {
                title: 'Tipo',
                dataKey: 'doc_type',
                render: (item: IVoucher) => DOC_TYPES[item.doc_type],
              },
              {
                title: 'Serie',
                dataKey: 'serie',
              },
              {
                title: 'Descripción',
                dataKey: 'description',
              },
              {
                title: 'Cliente',
                dataKey: 'customer_name',
                render: (item: IVoucher) => (
                  <>
                    <p>{item.customer_number}</p>
                    <p>{item.customer_name}</p>
                  </>
                ),
              },
              {
                title: 'FECHA',
                dataKey: 'created_at',
                render: (item: IVoucher) =>
                  formatDate(new Date(item.created_at), 'yyyy/MM/dd, hh:mm a', false),
              },
              {
                title: 'Descargas',
                dataKey: 'downloads',
                disableSort: true,
                render: (item: IVoucher) => (
                  <div className="flex space-x-2 pr-2">
                    {item.link_xml && (
                      <a href={item.link_xml} target="_blank" rel="noreferrer">
                        <SolidButton color={BUTTON_COLORS.BLUE}>XML</SolidButton>
                      </a>
                    )}
                    {item.link_pdf && (
                      <a href={item.link_pdf} target="_blank" rel="noreferrer">
                        <SolidButton color={BUTTON_COLORS.BLUE}>PDF</SolidButton>
                      </a>
                    )}
                    {item.link_cdr && (
                      <a href={item.link_cdr} target="_blank" rel="noreferrer">
                        <SolidButton color={BUTTON_COLORS.BLUE}>CDR</SolidButton>
                      </a>
                    )}
                  </div>
                ),
              },
              {
                title: 'Anulación',
                dataKey: 'void',
                disableSort: true,
                render: (item: IVoucher) => (
                  <div className="flex space-x-2 pr-2">
                    {item.status === VOUCHER_STATUS.SENT && (
                      <SolidButton
                        color={BUTTON_COLORS.RED}
                        onClick={() => {
                          setAction('void');
                          setIsActionOpen(true);
                          setActionData(item);
                        }}
                      >
                        Anular
                      </SolidButton>
                    )}
                    {item.cancel &&
                      (item.cancel.cdr ? (
                        <a href={item.cancel.link_cdr} target="_blank" rel="noreferrer">
                          <SolidButton color={BUTTON_COLORS.BLUE}>CDR</SolidButton>
                        </a>
                      ) : (
                        <a href={item.cancel.link_xml} target="_blank" rel="noreferrer">
                          <SolidButton color={BUTTON_COLORS.BLUE}>XML</SolidButton>
                        </a>
                      ))}
                  </div>
                ),
              },
              {
                title: 'OPCIONES',
                dataKey: 'options',
                disableSort: true,
                render: (item: IVoucher) => (
                  <div className="flex space-x-2 pr-2">
                    {item.status === VOUCHER_STATUS.PENDING && (
                      <SolidButton
                        color={BUTTON_COLORS.BLUE}
                        isLoading={loadingId === item._id}
                        onClick={() => {
                          submitVoucher(item._id, true);
                        }}
                      >
                        Emitir
                      </SolidButton>
                    )}
                    {item.status === VOUCHER_STATUS.SENT && (
                      <>
                        {!['C', 'D'].includes(item.doc_type) && (
                          <SolidButton
                            color={BUTTON_COLORS.BLUE}
                            onClick={() => {
                              setAction('note');
                              setIsActionOpen(true);
                              setActionData(item);
                              setValue('description', item.description);
                              setValue('change', item.change);
                              setValue('amount', item.amount);
                            }}
                          >
                            Nota
                          </SolidButton>
                        )}
                        <SolidButton
                          color={BUTTON_COLORS.BLUE}
                          onClick={() => {
                            setAction('mail');
                            setIsActionOpen(true);
                            setActionData(item);
                          }}
                        >
                          Enviar
                        </SolidButton>
                      </>
                    )}
                  </div>
                ),
              },
            ]}
            data={operations}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
